import { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { Crown, LayoutDashboard, MapPin, Bus, CalendarDays, ArrowRightLeft, Palette, CreditCard, BarChart3, ClipboardList, Building2, Megaphone, Loader2, Paperclip, X, Menu, FileSignature, Clock, FolderOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { readFileAsAttachment } from "@/components/ChatThread";
import UserMenu from "@/components/UserMenu";
import { LocationSwitcher } from "@/components/corporate/LocationSwitcher";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/corporate", label: "Overview", Icon: LayoutDashboard, end: true, testid: "corp-nav-overview" },
  { to: "/corporate/locations", label: "Locations", Icon: MapPin, testid: "corp-nav-locations" },
  { to: "/corporate/buses", label: "Buses", Icon: Bus, testid: "corp-nav-buses", kinds: ["school"] },
  { to: "/corporate/calendar", label: "Calendar", Icon: CalendarDays, testid: "corp-nav-calendar", kinds: ["school"] },
  { to: "/corporate/academics", label: "Periods & subjects", Icon: Clock, testid: "corp-nav-academics", kinds: ["school"] },
  { to: "/corporate/transfers", label: "Student transfers", Icon: ArrowRightLeft, testid: "corp-nav-transfers", kinds: ["school"] },
  { to: "/corporate/forms", label: "Forms", Icon: FileSignature, testid: "corp-nav-forms" },
  { to: "/corporate/files", label: "Files", Icon: FolderOpen, testid: "corp-nav-files" },
  { to: "/corporate/branding", label: "Branding", Icon: Palette, testid: "corp-nav-branding" },
  { to: "/corporate/billing", label: "Billing", Icon: CreditCard, testid: "corp-nav-billing" },
  { to: "/corporate/analytics", label: "Analytics", Icon: BarChart3, testid: "corp-nav-analytics" },
  { to: "/corporate/attendance", label: "Attendance log", Icon: ClipboardList, testid: "corp-nav-attendance" },
];

export function useCorporate() {
  return useOutletContext();
}

export default function CorporateLayout() {
  const nav = useNavigate();
  const [corp, setCorp] = useState(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const [pendingXfers, setPendingXfers] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const loadCorp = useCallback(() => {
    api.get("/corporate/me")
      .then(({ data }) => setCorp(data))
      .catch((e) => {
        if (e.response?.status === 403 || e.response?.status === 404) {
          toast.error("Corporate access required.");
          nav("/admin");
        }
      });
  }, [nav]);

  useEffect(() => {
    loadCorp();
    window.addEventListener("logerd:corporate-updated", loadCorp);
    return () => window.removeEventListener("logerd:corporate-updated", loadCorp);
  }, [loadCorp]);

  useEffect(() => {
    const check = () => {
      api.get("/corporate/transfer-requests")
        .then(({ data }) => setPendingXfers((data.incoming || []).filter((r) => r.status === "pending").length))
        .catch(() => {});
    };
    check();
    const iv = setInterval(check, 30000);
    window.addEventListener("logerd:xfers-updated", check);
    return () => { clearInterval(iv); window.removeEventListener("logerd:xfers-updated", check); };
  }, []);


  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="corporate-page">
      {mobileNavOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 md:hidden" data-testid="corp-mobile-overlay" onClick={() => setMobileNavOpen(false)} />
      )}
      <aside
        onClick={(e) => { if (e.target.closest("a,button")) setMobileNavOpen(false); }}
        className={`w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col fixed inset-y-0 z-50 overflow-y-auto transform transition-transform duration-200 md:translate-x-0 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex items-center gap-3 px-4 py-5 border-b border-slate-100">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl overflow-hidden flex-shrink-0 bg-white border border-slate-200 p-1">
            <img src={corp?.logo_data_url || "/logerd-logo.png"} alt={corp?.name || "Logerd"} className="h-full w-full object-contain" data-testid="corporate-header-logo" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold">{corp?.kind === "school" ? "District console" : corp?.kind === "care" ? "Care group console" : corp?.kind === "daycare" ? "Daycare group console" : "Corporate console"}</div>
            <div className="font-display text-base font-bold tracking-tight text-slate-900 truncate" data-testid="corporate-name-display">{corp?.name || "…"}</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter(({ kinds }) => !kinds || !corp || kinds.includes(corp.kind)).map(({ to, label, Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <Icon className="h-4 w-4" /> {label}
              {to === "/corporate/transfers" && pendingXfers > 0 && (
                <span className="ml-auto rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white" data-testid="corp-nav-transfers-badge">
                  {pendingXfers}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-slate-100 space-y-1">
          {corp?.kind === "school" && (
            <button
              onClick={() => setMsgOpen(true)}
              data-testid="district-message-btn"
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-amber-700 hover:bg-amber-50 transition"
            >
              <Megaphone className="h-4 w-4" /> Message parents
            </button>
          )}
          <button
            onClick={() => nav("/admin")}
            data-testid="corporate-to-admin"
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
          >
            <Building2 className="h-4 w-4" /> My location
          </button>
        </div>
      </aside>
      <div className="flex-1 md:ml-64 min-w-0">
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 bg-white border-b border-slate-200 px-4 py-3">
          <button onClick={() => setMobileNavOpen(true)} data-testid="corp-mobile-menu-btn"
                  className="h-9 w-9 rounded-lg border border-slate-200 inline-flex items-center justify-center text-slate-700">
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-display font-bold text-slate-900 truncate flex-1">{corp?.name || "Corporate console"}</span>
          <UserMenu roleLabel="Corporate Admin" />
        </div>
        <div className="hidden md:flex items-center justify-end gap-3 px-6 pt-3" data-testid="corporate-topbar">
          <LocationSwitcher />
          <UserMenu roleLabel="Corporate Admin" />
        </div>
        <Outlet context={{ corp, reloadCorp: loadCorp }} />
      </div>
      <DistrictMessageDialog open={msgOpen} onOpenChange={setMsgOpen} />
    </div>
  );
}

function DistrictMessageDialog({ open, onOpenChange }) {
  const [body, setBody] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [busy, setBusy] = useState(false);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) readFileAsAttachment(f, setAttachment, (err) => toast.error(err));
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() && !attachment) return;
    if (!confirm("Send this message to the parents of EVERY student in ALL your schools?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/corporate/messages/send", {
        body: body.trim(),
        attachment: attachment || undefined,
      });
      toast.success(`District message sent to parents of ${data.sent_to} students`);
      setBody("");
      setAttachment(null);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="district-message-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-amber-600" /> District message
          </DialogTitle>
          <DialogDescription>
            Goes to the parents of every student in all your schools. Replies route to each child's class teacher.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-3 pt-1">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            autoFocus
            data-testid="district-broadcast-input"
            placeholder="e.g. All district schools closed Monday for staff development"
            className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
          />
          {attachment && (
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-medium" data-testid="district-attachment-chip">
              <Paperclip className="h-3 w-3" /> {attachment.name}
              <button type="button" onClick={() => setAttachment(null)} data-testid="district-attachment-remove"><X className="h-3 w-3" /></button>
            </span>
          )}
          <DialogFooter className="gap-2 sm:gap-2 sm:justify-between">
            <label className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 inline-flex items-center gap-2 cursor-pointer hover:bg-slate-50 transition" title="Attach a photo or PDF">
              <Paperclip className="h-4 w-4" /> Attach file
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={pickFile} data-testid="district-file-input" />
            </label>
            <button
              type="submit"
              disabled={busy || (!body.trim() && !attachment)}
              data-testid="district-broadcast-send"
              className="h-10 rounded-lg bg-amber-600 px-5 text-sm font-medium text-white inline-flex items-center justify-center gap-2 hover:bg-amber-700 disabled:opacity-50 transition"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />} Send to district
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
